import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type GetImageMixV1PortfoliosPortfolioIdPdpsPdpIdImageMixGetData,
  type GetImageMixV1PortfoliosPortfolioIdPdpsPdpIdImageMixGetError,
  type GetImageMixV1PortfoliosPortfolioIdPdpsPdpIdImageMixGetResponse,
  getImageMixV1PortfoliosPortfolioIdPdpsPdpIdImageMixGet,
} from '../../generated';

export type GetImageMixQueryVariables = GetImageMixV1PortfoliosPortfolioIdPdpsPdpIdImageMixGetData['path'];

export type GetImageMixQuery = GetImageMixV1PortfoliosPortfolioIdPdpsPdpIdImageMixGetResponse;

export const useGetImageMixQuery = <
  TData = GetImageMixQuery,
  TError = GetImageMixV1PortfoliosPortfolioIdPdpsPdpIdImageMixGetError,
>(
  { portfolio_id, pdp_id }: GetImageMixQueryVariables,
  options?: Omit<UseQueryOptions<GetImageMixQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetImageMixQuery, TError, TData>({
    queryKey: ['GetImageMix', { portfolio_id, pdp_id }],
    queryFn: async () => {
      const { data } = await getImageMixV1PortfoliosPortfolioIdPdpsPdpIdImageMixGet({
        path: { portfolio_id, pdp_id },
        throwOnError: true,
      });
      return data as GetImageMixQuery;
    },
    ...options,
  });
};

useGetImageMixQuery.getKey = ({ portfolio_id, pdp_id }: GetImageMixQueryVariables) => [
  'GetImageMix',
  { portfolio_id, pdp_id },
];

useGetImageMixQuery.fetcher = (
  { portfolio_id, pdp_id }: GetImageMixQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data } = await getImageMixV1PortfoliosPortfolioIdPdpsPdpIdImageMixGet({
      path: { portfolio_id, pdp_id },
      throwOnError: true,
    });
    return data as GetImageMixQuery;
  };
};

useGetImageMixQuery.schema = {
  method: 'get',
  path: '/v1/portfolios/{portfolio_id}/pdps/{pdp_id}/image-mix',
};
